/// <reference path="../types.js" />

import stateObj from '../state.js';

/**
 * @template {HTMLElement} T
 */
export default class BaseComponent {
  /**
   * @type {T}
   * @protected
   */
  element;

  /**
   * @type {BaseComponent<HTMLElement>[]}
   * @protected
   */
  childComponents = [];

  /** @type {StateObj} */
  state = stateObj;

  /**
   * Creates a new base component.
   * @param {Props<T>} props
   * @param {...(HTMLElement | BaseComponent<HTMLElement>)} children
   */
  constructor(props, ...children) {
    const elementTag = props.elementTag || 'div';
    this.element = /** @type {T} */ (document.createElement(elementTag));

    if (children && children.length > 0) {
      this.appendChildren(...children);
    }

    if (props.text) {
      this.setText(props.text);
    }

    if (props.classes && props.classes.length > 0) {
      this.addClasses(...props.classes);
    }

    Object.assign(this.element, props);
  }

  /**
   * Adds child elements to element property.
   * @param {...(HTMLElement | BaseComponent<HTMLElement>)} children
   * @returns {BaseComponent}
   */
  appendChildren(...children) {
    children.forEach((child) => this.appendSingle(child));

    return this;
  }

  /**
   * Adds a single element to element property.
   * @param {HTMLElement | BaseComponent<HTMLElement>} child
   * @returns {BaseComponent}
   */
  appendSingle(child) {
    if (child instanceof BaseComponent) {
      this.element.append(child.getElement());
      this.childComponents.push(child);
    } else if (child instanceof HTMLElement) {
      this.element.append(child);
    }

    return this;
  }

  /**
   * Returns the component's HTML element.
   * @returns {T}
   */
  getElement() {
    return this.element;
  }

  /**
   * Removes child components and the component itself.
   * @returns {void}
   */
  removeSelf() {
    this.removeChildren();
    this.element.remove();
  }

  /**
   * Removes child components from the component.
   * @returns {BaseComponent}
   */
  removeChildren() {
    this.childComponents.forEach((child) => child.removeSelf());
    this.childComponents.length = 0;

    return this;
  }

  /**
   * Adds CSS classes to the component's element.
   * @param {...string} classes
   * @returns {BaseComponent}
   */
  addClasses(...classes) {
    this.element.classList.add(...classes);

    return this;
  }

  /**
   * Removes CSS classes from the component's element.
   * @param {...string} classes
   * @returns {BaseComponent}
   */
  removeClasses(...classes) {
    this.element.classList.remove(...classes);

    return this;
  }

  /**
   * Toggles the component's CSS classes.
   * @param {...string} classes
   * @returns {BaseComponent}
   */
  toggleClasses(...classes) {
    classes.forEach((className) => {
      this.element.classList.toggle(className);
    });

    return this;
  }

  /**
   * Sets the component's text content.
   * @param {string} text
   * @returns {BaseComponent}
   */
  setText(text) {
    this.element.textContent = text;

    return this;
  }

  /**
   * Adds event listener.
   * @param {keyof DocumentEventMap | CustomEventMap} eventType
   * @param {EventListenerOrEventListenerObject} handler
   * @param {AddEventListenerOptions} [options]
   * @returns {BaseComponent}
   */
  addListener(eventType, handler, options) {
    this.element.addEventListener(eventType, handler, options);

    return this;
  }

  /**
   * Removes event listener.
   * @param {keyof DocumentEventMap | CustomEventMap} eventType
   * @param {EventListenerOrEventListenerObject} handler
   * @returns {BaseComponent}
   */
  removeListener(eventType, handler) {
    this.element.removeEventListener(eventType, handler);

    return this;
  }
}
